import { useLingui } from '@lingui/react';
import type { I18n } from '@lingui/core';
import { Link } from 'react-router';
import { ExternalLink, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/utils/invoiceFormatters';
import { formatDateDisplay, formatDateTime } from '@/lib/format';
import type { ContractTerminationPayload as ContractTerminationPayloadModel } from '@/types/contractTermination';
import { ChangeRequestPayload } from './ChangeRequestPayload';
import { FullRecordRequestPayload } from './FullRecordRequestPayload';
import { GenericPayload } from './GenericPayload';
import { PickupExceptionPayload } from './PickupExceptionPayload';
import { PortfolioConversationDuePayload } from './PortfolioConversationDuePayload';
import { SplitAlertPayload } from './SplitAlertPayload';
import { CoverageBreachPayload } from './CoverageBreachPayload';
import { DiaperCheckPayload } from './DiaperCheckPayload';
import { ActivityGapPayload } from './ActivityGapPayload';
import { SubsidyReportPayload } from './SubsidyReportPayload';
import { SubsidyExpiryPayload } from './SubsidyExpiryPayload';
import { LateExtrasPayload } from './LateExtrasPayload';
import { ShiftRequestPayload } from './ShiftRequestPayload';
import { AccidentSeverityBadge } from './AccidentSeverityBadge';
import { getAbsenceTypeLabel } from '@/components/absences/AbsenceTypeBadge';
import { AbsenceType } from '@/types/employeeAbsences';
import { useEmployeePickerNames } from '@/hooks/useEmployees';
import { ChildCard } from '@/components/billing/contracts/ChildCard';

interface WorkflowPayloadDetailProps {
  category: string;
  payload: Record<string, unknown>;
  userNameMap: Map<string, string>;
  itemId?: string;
  /** Workflow item current_state — used to label the termination CTA. */
  currentState?: string;
  /** FSM actions the API currently offers — gates the change_request triage buttons. */
  availableActions?: string[];
  /** Workflow item due_at (ISO 8601): rendered by payloads that show a deadline. */
  dueAt?: string;
  /** Opens another workflow item in the sheet (a change request's follow-up case). */
  onOpenItem?: (itemId: string) => void;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function AccidentPayload({
  payload,
  userNameMap,
  i18n,
}: {
  payload: Record<string, unknown>;
  userNameMap: Map<string, string>;
  i18n: { _: (d: { id: string; message: string }) => string };
}) {
  const severity = payload.severity as string | undefined;
  const summary = payload.summary as string | undefined;
  const recordedBy = payload.recorded_by as string | undefined;
  const activityId = payload.activity_id as string | undefined;
  const childId = payload.child_id as string | undefined;

  return (
    <div className="space-y-4">
      {childId && (
        <FieldRow label={i18n._({ id: 'workflow.detail.child', message: 'Child' })}>
          <ChildCard childId={childId} />
        </FieldRow>
      )}
      {summary && (
        <FieldRow label={i18n._({ id: 'workflow.detail.summary', message: 'Summary' })}>
          <p className="text-sm">{summary}</p>
        </FieldRow>
      )}
      {severity && (
        <FieldRow label={i18n._({ id: 'workflow.detail.severity', message: 'Severity' })}>
          <AccidentSeverityBadge severity={severity} />
        </FieldRow>
      )}
      {recordedBy && (
        <FieldRow label={i18n._({ id: 'workflow.detail.recordedBy', message: 'Recorded By' })}>
          <p className="text-sm">
            {userNameMap.get(recordedBy) ?? i18n._({ id: 'common.unknown', message: 'Unknown' })}
          </p>
        </FieldRow>
      )}
      {activityId && (
        <Button asChild className="w-full" size="sm" variant="outline">
          <Link to={`/activities?activity=${encodeURIComponent(activityId)}`}>
            <FileText className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            {i18n._({ id: 'workflow.detail.openAccidentRecord', message: 'Open accident record' })}
          </Link>
        </Button>
      )}
    </div>
  );
}

function LeaveRequestPayload({
  payload,
  userNameMap,
  i18n,
}: {
  payload: Record<string, unknown>;
  userNameMap: Map<string, string>;
  i18n: { _: (d: { id: string; message: string }) => string };
}) {
  const employeeId = payload.employee_id as string | undefined;
  const leaveType = payload.leave_type as string | undefined;
  const startDate = payload.start_date as string | undefined;
  const endDate = payload.end_date as string | undefined;
  const reason = payload.reason as string | undefined;
  const { names: employeeNames } = useEmployeePickerNames();

  // No producer writes leave_request items yet, so the id may be an employee id
  // or a Clerk user id: try both maps, and never show the raw id.
  const employeeName = employeeId
    ? (employeeNames.get(employeeId) ??
      (userNameMap.has(employeeId) ? userNameMap.get(employeeId) : undefined) ??
      i18n._({ id: 'common.unknown', message: 'Unknown' }))
    : undefined;
  const normalizedLeaveType = leaveType?.toUpperCase();
  const leaveTypeLabel =
    normalizedLeaveType && (Object.values(AbsenceType) as string[]).includes(normalizedLeaveType)
      ? getAbsenceTypeLabel(normalizedLeaveType, i18n)
      : leaveType;

  return (
    <div className="space-y-4">
      {employeeName && (
        <FieldRow label={i18n._({ id: 'workflow.payload.employee', message: 'Employee' })}>
          <p className="text-sm">{employeeName}</p>
        </FieldRow>
      )}
      {leaveTypeLabel && (
        <FieldRow label={i18n._({ id: 'workflow.payload.leaveType', message: 'Leave Type' })}>
          <p className="text-sm">{leaveTypeLabel}</p>
        </FieldRow>
      )}
      {(startDate || endDate) && (
        <FieldRow label={i18n._({ id: 'workflow.payload.dateRange', message: 'Date Range' })}>
          <p className="text-sm">
            {startDate && endDate
              ? `${formatDateDisplay(startDate)} – ${formatDateDisplay(endDate)}`
              : formatDateDisplay(startDate ?? endDate)}
          </p>
        </FieldRow>
      )}
      {reason && (
        <FieldRow label={i18n._({ id: 'workflow.payload.reason', message: 'Reason' })}>
          <p className="text-sm">{reason}</p>
        </FieldRow>
      )}
    </div>
  );
}

function ContractTerminationPayload({
  payload,
  userNameMap,
  itemId,
  currentState,
  i18n,
}: {
  payload: Partial<ContractTerminationPayloadModel>;
  userNameMap: Map<string, string>;
  itemId?: string;
  currentState?: string;
  i18n: I18n;
}) {
  const mode = payload.mode;
  const childName = payload.child_name;
  const childInternalId = payload.child_internal_id;
  const requestedEndDate = payload.requested_termination_date;
  const effectiveEndDate = payload.effective_end_date;
  const reason = payload.reason;
  const requestedBy = payload.requested_by;
  const fees = payload.fees ?? [];
  const result = payload.result ?? {};
  const invoicesCancelled = Array.isArray(result.invoices_cancelled) ? result.invoices_cancelled.length : 0;
  const invoiceReissued = result.invoice_reissued as string | undefined;
  // Terminal workflow states (completed/cancelled/rejected) open the Review
  // screen in read-only mode; relabel the CTA so admins don't expect to act.
  const isTerminal =
    currentState === 'completed' ||
    currentState === 'cancelled' ||
    currentState === 'rejected' ||
    currentState === 'revoked';
  // #1678: a revoked termination carries the revoke record.
  const revoke = payload.revoke;
  const skipped = revoke?.allocations_skipped ?? [];

  return (
    <div className="space-y-4">
      {mode && (
        <FieldRow label={i18n._({ id: 'workflow.payload.mode', message: 'Mode' })}>
          <Badge variant={mode === 'cancellation_before_start' ? 'secondary' : 'outline'}>
            {mode === 'cancellation_before_start'
              ? i18n._({ id: 'termination.mode.cancellationBeforeStart', message: 'Cancellation before start' })
              : i18n._({ id: 'termination.mode.termination', message: 'Termination' })}
          </Badge>
        </FieldRow>
      )}
      {(childName || childInternalId) && (
        <FieldRow label={i18n._({ id: 'workflow.payload.childName', message: 'Child' })}>
          <p className="text-sm">
            {childName}
            {childInternalId ? ` (${childInternalId})` : ''}
          </p>
        </FieldRow>
      )}
      {requestedEndDate && (
        <FieldRow label={i18n._({ id: 'workflow.payload.requestedEndDate', message: 'Requested end date' })}>
          <p className="text-sm">{formatDateDisplay(requestedEndDate)}</p>
        </FieldRow>
      )}
      {effectiveEndDate && (
        <FieldRow label={i18n._({ id: 'workflow.payload.effectiveEndDate', message: 'Effective end date' })}>
          <p className="text-sm">{formatDateDisplay(effectiveEndDate)}</p>
        </FieldRow>
      )}
      {reason && (
        <FieldRow label={i18n._({ id: 'workflow.payload.reason', message: 'Reason' })}>
          <p className="text-sm">{reason}</p>
        </FieldRow>
      )}
      {requestedBy && (
        <FieldRow label={i18n._({ id: 'workflow.payload.requestedBy', message: 'Requested by' })}>
          <p className="text-sm">{userNameMap.get(requestedBy) ?? requestedBy}</p>
        </FieldRow>
      )}
      {fees.length > 0 && (
        <FieldRow label={i18n._({ id: 'workflow.payload.fees', message: 'Exit fees' })}>
          <ul className="text-sm space-y-1">
            {fees.map((fee, idx) => (
              <li key={idx} className="flex justify-between gap-2">
                <span>{String(fee.label ?? '')}</span>
                <span className="font-medium">{formatCurrency(Number(fee.amount_cents ?? 0))}</span>
              </li>
            ))}
          </ul>
        </FieldRow>
      )}
      {(invoicesCancelled > 0 || invoiceReissued) && (
        <FieldRow label={i18n._({ id: 'workflow.payload.result', message: 'Result' })}>
          <p className="text-sm">
            {i18n._({ id: 'termination.payload.invoicesCancelled', message: 'Invoices cancelled' })}: {invoicesCancelled}
            {invoiceReissued ? ` · ${i18n._({ id: 'termination.payload.invoiceReissued', message: 'Re-issued draft' })}` : ''}
          </p>
        </FieldRow>
      )}
      {currentState === 'revoked' && (
        <FieldRow label={i18n._({ id: 'termination.payload.revoked', message: 'Revoked' })}>
          <div className="space-y-1 text-sm" data-testid="termination-revoke-result">
            <p>
              {revoke?.completed === false
                ? i18n._({ id: 'termination.payload.revokeIncomplete', message: 'The revoke stopped part-way; revoke again to complete it.' })
                : i18n._({ id: 'termination.payload.revokeDone', message: 'The termination was revoked; the contract continues.' })}
            </p>
            {revoke?.at && (
              <p className="text-muted-foreground">
                {formatDateTime(revoke.at)}
                {revoke.by ? ` · ${userNameMap.get(revoke.by) ?? revoke.by}` : ''}
              </p>
            )}
            {revoke?.new_end_date && (
              <p>
                {i18n._({ id: 'termination.payload.revokeNewEnd', message: 'New end date' })}: {formatDateDisplay(revoke.new_end_date)}
              </p>
            )}
            {revoke?.reason && <p>{revoke.reason}</p>}
            {skipped.length > 0 && (
              <p className="text-warning-on-surface">
                {i18n._({
                  id: 'termination.payload.revokeSkipped',
                  message: '{count, plural, one {# group place needs to be assigned by hand} other {# group places need to be assigned by hand}}',
                  values: { count: skipped.length },
                })}
              </p>
            )}
          </div>
        </FieldRow>
      )}
      {itemId && (
        <Button asChild className="w-full" size="sm" variant={isTerminal ? 'outline' : 'default'}>
          <Link to={`/contract-terminations/${encodeURIComponent(itemId)}/review`}>
            {isTerminal
              ? i18n._({ id: 'termination.payload.viewTermination', message: 'View termination' })
              : i18n._({ id: 'termination.payload.openReview', message: 'Open Review' })}
            <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </Button>
      )}
    </div>
  );
}

export function WorkflowPayloadDetail({ category, payload, userNameMap, itemId, currentState, availableActions, dueAt, onOpenItem }: WorkflowPayloadDetailProps) {
  const { i18n } = useLingui();

  switch (category) {
    case 'accident_notification':
      return <AccidentPayload payload={payload} userNameMap={userNameMap} i18n={i18n} />;
    case 'leave_request':
      return <LeaveRequestPayload payload={payload} userNameMap={userNameMap} i18n={i18n} />;
    case 'contract_termination':
      return <ContractTerminationPayload payload={payload as Partial<ContractTerminationPayloadModel>} userNameMap={userNameMap} itemId={itemId} currentState={currentState} i18n={i18n} />;
    case 'change_request':
      return (
        <ChangeRequestPayload
          payload={payload}
          itemId={itemId}
          currentState={currentState}
          availableActions={availableActions}
          i18n={i18n}
          onOpenItem={onOpenItem}
        />
      );
    case 'pickup_exception':
      return <PickupExceptionPayload payload={payload} userNameMap={userNameMap} i18n={i18n} />;
    case 'portfolio_full_record_request':
      return <FullRecordRequestPayload payload={payload} i18n={i18n} locale={i18n.locale} />;
    case 'portfolio_conversation_due':
      return <PortfolioConversationDuePayload payload={payload} dueAt={dueAt} i18n={i18n} />;
    case 'split_alert':
      return <SplitAlertPayload payload={payload} i18n={i18n} />;
    case 'coverage_breach':
      return <CoverageBreachPayload payload={payload} i18n={i18n} />;
    case 'diaper_check_overdue':
      return <DiaperCheckPayload payload={payload} i18n={i18n} />;
    case 'activity_gap':
      return <ActivityGapPayload payload={payload} i18n={i18n} />;
    case 'subsidy_report':
      return <SubsidyReportPayload itemId={itemId} currentState={currentState} i18n={i18n} />;
    case 'subsidy_expiry':
      return <SubsidyExpiryPayload payload={payload} i18n={i18n} />;
    case 'late_extras':
      return <LateExtrasPayload payload={payload} itemId={itemId} currentState={currentState} />;
    case 'shift_request':
      return <ShiftRequestPayload payload={payload} i18n={i18n} />;
    default:
      return <GenericPayload payload={payload} i18n={i18n} />;
  }
}
